import {
  Box,
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  StackProps,
  Text,
} from "@chakra-ui/react";
import { runIfFn } from "@chakra-ui/utils";
import React, { ReactElement } from "react";
import { To } from "react-router-dom";
import { RouteLink } from "shared/components/link/RouteLink";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { LinkIcon } from "shared/icons/LinkIcon";
import { SmallArrowDownIcon } from "shared/icons/SmallArrowDownIcon";
import { getValidChildren } from "shared/utils/ReactUtils";

export const Metadata: React.FC<React.PropsWithChildren<StackProps>> = ({
  children,
  ...props
}) => (
  <HStack
    className="mb-metadata"
    spacing="4px"
    divider={<LinkIcon borderLeftWidth="0 !important" />}
    sx={{
      color: "gray.500",
      textStyle: "small",
      lineHeight: "20px",
      spacing: "4px",
      ".mb-metadata-item-link": {
        lineHeight: "16px",
        display: "inline-flex",
        textDecoration: "none",
        color: "inherit",
        ".mb-metadata-item-link-icon": {
          display: "none",
          w: "16px",
          h: "16px",
        },
        _hover: {
          fontWeight: "medium",
          color: "teal.700",
          textDecoration: "none",
          ".mb-metadata-item-link-icon": {
            display: "inline-block",
          },
        },
        _active: {
          color: "teal.900",
        },
      },
      ".mb-metadata-item-title": {
        lineHeight: "16px",
        _last: {
          fontWeight: 500,
          color: "gray.800",
        },
      },
      ".mb-metadata-item-with-label": {
        lineHeight: "16px",
        ".mb-metadata-item-label": {
          _after: {
            content: '"\\0000a0"',
          },
        },
        ".mb-metadata-item-link": {
          fontWeight: 500,
          color: "gray.700",
          _hover: {
            color: "teal.700",
          },
          _active: {
            color: "teal.900",
          },
        },
      },
      ".mb-metadata-item-menu-button": {
        "&:hover, &[data-active]": {
          svg: {
            color: "primary.500",
          },
        },
      },
      ".mb-metadata-item-menu-list": {
        minWidth: "unset",
        ".mb-metadata-item-menu-item": {
          textStyle: "small",
        },
      },
    }}
    {...props}
  >
    {children}
  </HStack>
);

export interface MetadataItemProps {
  title: string | ((count: number | undefined) => string);
  to?: To;
  flattenIfSingle?: boolean;
  label?: string;
}

export const MetadataItem: React.FC<
  React.PropsWithChildren<MetadataItemProps>
> = ({ title, label, to, flattenIfSingle, children }) => {
  const validChildren = getValidChildren(children);
  const renderedTitle = runIfFn(title, validChildren.length);

  if (validChildren.length === 1 && flattenIfSingle) {
    const firstChild = validChildren[0];
    return <MetadataItem {...firstChild.props} />;
  }

  if (validChildren.length > 0) {
    return (
      <Menu
        offset={[0, 4]}
        placement="bottom-end"
        isLazy={true}
        lazyBehavior="unmount"
      >
        <MenuButton className="mb-metadata-item-menu-button">
          <HStack spacing="0">
            <Box>{renderedTitle}</Box>
            <Center w="16px" h="16px">
              <SmallArrowDownIcon />
            </Center>
          </HStack>
        </MenuButton>
        <MenuList className="mb-metadata-item-menu-list">
          {validChildren.map(
            (child: ReactElement<MetadataItemProps>, index) => {
              const { title, to } = child.props;
              return (
                <MenuItem
                  key={index}
                  as={RouteLink}
                  className="mb-metadata-item-menu-item"
                  colorScheme="none"
                  to={to}
                >
                  {runIfFn(title)}
                </MenuItem>
              );
            },
          )}
        </MenuList>
      </Menu>
    );
  }
  if (to) {
    if (label) {
      return (
        <Text as="div" className="mb-metadata-item-with-label">
          <Text as="span" className="mb-metadata-item-label">
            {label}
          </Text>
          <RouteLink
            className="mb-metadata-item-link"
            colorScheme="none"
            to={to}
          >
            {renderedTitle}
            <ArrowUpRightIcon className="mb-metadata-item-link-icon" />
          </RouteLink>
        </Text>
      );
    }
    return (
      <RouteLink className="mb-metadata-item-link" to={to}>
        {renderedTitle}
        <ArrowUpRightIcon className="mb-metadata-item-link-icon" />
      </RouteLink>
    );
  }
  return <Text className="mb-metadata-item-title">{renderedTitle}</Text>;
};
